// src/hooks/useColumnAssignments.js
import { useMemo } from 'react';
import { buildEventLocatorKey } from '../utils/dateHelpers';

/**
 * Hook do przypisywania kolumn eventom w widoku
 */
export function useColumnAssignments(datesWithEvents, nonEmptyEventsByDate) {
    return useMemo(() => {
        if (!datesWithEvents.length) return {};

        const seriesMap = new Map();
        const seriesMeta = new Map();
        let maxPerDay = 0;

        datesWithEvents.forEach((date) => {
            const list = nonEmptyEventsByDate[date] || [];
            if (list.length > maxPerDay) maxPerDay = list.length;

            list.forEach((ev) => {
                const eventName = ev?.event || "";
                const calId = ev?.calendarId || "noCal";
                const gId = ev?.gId || "";
                const seriesId = gId ? `gid:${calId}::${gId}` : `${eventName}::${calId}`;
                if (!seriesMap.has(seriesId)) {
                    seriesMap.set(seriesId, new Set());
                    seriesMeta.set(seriesId, { eventName, calendarId: ev?.calendarId, gId: gId || undefined });
                }
                seriesMap.get(seriesId).add(date);
            });
        });

        const seriesList = Array.from(seriesMap.entries()).map(([id, set]) => {
            const dates = Array.from(set).sort();
            return {
                id,
                dates,
                length: dates.length,
                first: dates[0],
                meta: seriesMeta.get(id),
            };
        });
        seriesList.sort((a, b) => {
            if (b.length !== a.length) return b.length - a.length;
            return a.first.localeCompare(b.first);
        });

        let maxCols = Math.max(1, maxPerDay);
        const occupiedByDate = new Map();
        const assignMap = new Map();

        const ensureSet = (d) => {
            if (!occupiedByDate.has(d)) occupiedByDate.set(d, new Set());
            return occupiedByDate.get(d);
        };

        const canUseLaneForSeries = (lane, dates) => dates.every((d) => !ensureSet(d).has(lane));

        const occupyLaneForSeries = (lane, id, dates, meta) => {
            dates.forEach((d) => {
                ensureSet(d).add(lane);
                const key = buildEventLocatorKey(d, {
                    gId: meta.gId,
                    event: meta.eventName,
                    calendarId: meta.calendarId || "noCal",
                });
                assignMap.set(key, lane);
            });
        };

        seriesList.forEach((s) => {
            let chosen = null;
            for (let lane = 0; lane < maxCols; lane++) {
                if (canUseLaneForSeries(lane, s.dates)) {
                    chosen = lane;
                    break;
                }
            }
            if (chosen === null) {
                chosen = maxCols;
                maxCols += 1;
            }
            occupyLaneForSeries(chosen, s.id, s.dates, s.meta);
        });

        const out = {};
        assignMap.forEach((lane, key) => {
            out[key] = lane;
        });
        datesWithEvents.forEach((date) => {
            const used = occupiedByDate.get(date) || new Set();
            for (let col = 0; col < maxCols; col++) {
                if (!used.has(col)) {
                    out[`${date}::__placeholder__::${col}`] = col;
                }
            }
        });

        return out;
    }, [datesWithEvents, nonEmptyEventsByDate]);
}

